import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Table } from 'antd'
import PerText from '../../../components/custom_components/Text'
import useReplyerMeetingsHistory from '../../../graphql/meeting/hooks/useReplyerMeetingsHistory'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { MeetingTag } from '../../../components/custom_components/MeetingTag'
import { SortOrder } from 'antd/lib/table'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { PerAvatarPlus } from '../../../components/custom_components/AvatarPlus'
import { MeetingType, EmployeeDisplayTinyInfo, RoleName, Meeting, MeetingStatus, EmployeeDisplayShortInfo } from '../../../types'
import { NewMeetingInviteButton } from '../invite/NewMeetingInviteButton'
import { RequestMeetingInviteButton } from '../invite/RequestMeetingInviteButton'
import { PdfDownloadPopover } from '../../../components/custom_components/PdfDownloadPopover/PdfDownloadPopover'
import { PdfObjectiveAttachmentPopover } from '../../../components/custom_components/PdfObjectiveAttachmentPopover/PdfObjectiveAttachmentPopover'
import PerIcon from '../../../components/custom_components/Icon'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'

interface MeetingsHistoryTableContainerProps {
  employeeId: string
  employee?: EmployeeDisplayTinyInfo
  employeeFirstName?: string
  showReviewer?: boolean
  light?: boolean
  employeeIsEditable?: boolean
  emptyTextType?: 'request' | 'meetingEmployee' | 'meetingReviewer' | 'publicProfile'
  resignedView?: boolean
  userCannotRequestMeetings?: boolean
  view?: 'PublicMeetingsTab' | 'EmployeeMeetingsContainer'
}

export const MeetingsHistoryTableContainer: FC<MeetingsHistoryTableContainerProps> = ({
  employeeId,
  employeeFirstName,
  light,
  emptyTextType = 'request',
  employee,
  employeeIsEditable,
  resignedView,
  userCannotRequestMeetings,
  view,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatDateShort, compareDates } = useDateTime(user)

  const { data, loading, error } = useReplyerMeetingsHistory(employeeId, resignedView)

  const canViewPreparation = (user.roles && user.roles.includes(RoleName.Hr)) || user.employeeId === employeeId

  if (loading || error || !data || !data.meetingsHistoryByReplyer) {
    return view && ['EmployeeMeetingsContainer', 'PublicMeetingsTab'].includes(view) ? (
      <BackgroundBox r height={['EmployeeMeetingsContainer'].includes(view) ? 'withTabsAndInfoBox' : 'profileView'}>
        <NewLoadingAndErrors
          isLoading={loading}
          error={error}
          hasNoData={!data || !data.meetingsHistoryByReplyer}
          showSpinner
          t={t}
          customErrorMessage={t('page.error.pageUnavailable.customMessage.meetingHistory')}
        />
      </BackgroundBox>
    ) : (
      <NewLoadingAndErrors
        backgroundCardHeight={270}
        isLoading={loading}
        error={error}
        hasNoData={!data || !data.meetingsHistoryByReplyer}
        showSpinner
        t={t}
        customErrorMessage={t('page.error.pageUnavailable.customMessage.meetingHistory')}
      />
    )
  }

  const meetings: Meeting[] = data.meetingsHistoryByReplyer.map((meeting) => {
    return (meeting.reviewer ? meeting : { ...meeting, confirmationDate: undefined }) as Meeting
  })

  const isEmployeeOrEmptyEmployee =
    (employeeIsEditable && employee !== undefined) || (employee === undefined && user.employeeId === employeeId)

  const columns = [
    {
      key: 'meetingHistory-date',
      title: t('appraisalMeeting.stepOverview.history.date'),
      className: 'minWidth-column-l',
      sorter: (meetingA: Meeting, meetingB: Meeting) => {
        return compareDates(meetingA.displayDate, meetingB.displayDate)
      },
      defaultSortOrder: 'descend' as SortOrder,
      render: (meeting: Meeting) => <PerText>{formatDateShort(meeting.displayDate)}</PerText>,
    },
    {
      key: 'meetingHistory-season',
      title: t('appraisalMeeting.stepOverview.history.season'),
      className: 'minWidth-column-s',
      sorter: (meetingA: Meeting, meetingB: Meeting) => {
        const comparison = meetingA.appraisalSeason.year - meetingB.appraisalSeason.year
        if (comparison === 0) return compareDates(meetingA.displayDate, meetingB.displayDate)
        else return comparison
      },
      render: (meeting: Meeting) => <PerText>{meeting.appraisalSeason.year}</PerText>,
    },
    {
      key: 'meetingHistory-type',
      title: t('appraisalMeeting.stepOverview.history.type'),
      className: 'minWidth-column-m',
      dataIndex: 'type',
      render: (dataIndex: MeetingType) => <MeetingTag type={dataIndex} />,
    },
    {
      key: 'meetingHistory-closingDate',
      title: t('appraisalMeeting.stepOverview.history.closingDate'),
      className: 'minWidth-column-l',
      render: (meeting: Meeting) => {
        const text = meeting.status === MeetingStatus.DisputedConfirmed ? 'disputedText' : 'confirmedText'
        if ([MeetingStatus.DisputedConfirmed, MeetingStatus.PendingConfirmation].includes(meeting.status)) {
          const tooltip = meeting.status === MeetingStatus.DisputedConfirmed ? 'disputedTooltip' : 'unconfirmedTooltip'
          return (
            <PerFlexRow align="middle" direction="row" noWrap className="fullHeight">
              <PerFlexRow>
                <PerIcon
                  tooltip={t(`appraisalMeeting.stepOverview.history.${tooltip}`)}
                  name="check_stroke"
                  variant="decoration"
                  color={text === 'disputedText' ? 'error' : 'success'}
                  size={14}
                />
              </PerFlexRow>
              <PerFlexRow direction="row" ml="xxs">
                <PerText fontSize="xs">
                  {meeting.confirmationDate
                    ? ` ${t(`appraisalMeeting.stepOverview.history.${text}`)} ${formatDateShort(meeting.confirmationDate)}`
                    : '-'}
                </PerText>
              </PerFlexRow>
            </PerFlexRow>
          )
        }
        const possibleForceConfirmationReasons = [
          t('forceConfirmationModal.parentalLeave'),
          t('forceConfirmationModal.longtermSickness'),
          t('forceConfirmationModal.sabbatical'),
          t('forceConfirmationModal.leaveOfAbsence'),
        ]
        return (
          <PerFlexRow align="middle" direction="row" noWrap className="fullHeight">
            <PerFlexRow>
              <PerIcon name="check_stroke" variant="decoration" color="success" size={14} />
            </PerFlexRow>
            <PerFlexRow direction="row" ml="xxs">
              <PerText fontSize="xs">
                {meeting.confirmationDate ? (
                  meeting.forceConfirmationEmployee && meeting.forceConfirmationReason ? (
                    <>
                      <PerText>
                        {t(`appraisalMeeting.stepOverview.history.forceConfirmed`, {
                          managerFullName: computeFullname(
                            user.nameDisplayPreference,
                            meeting.forceConfirmationEmployee.firstName,
                            meeting.forceConfirmationEmployee.lastName,
                          ),
                          date: formatDateShort(meeting.confirmationDate),
                          employeeFirstName: meeting.employee.legalFirstName,
                        })}
                      </PerText>
                      <PerText>
                        {t(`appraisalMeeting.stepOverview.history.forceConfirmedReason`, {
                          reason: possibleForceConfirmationReasons.includes(meeting.forceConfirmationReason)
                            ? meeting.forceConfirmationReason
                            : t('forceConfirmationModal.other'),
                        })}
                      </PerText>
                    </>
                  ) : (
                    ` ${t(`appraisalMeeting.stepOverview.history.${text}`)} ${formatDateShort(meeting.confirmationDate)}`
                  )
                ) : (
                  '-'
                )}
              </PerText>
            </PerFlexRow>
          </PerFlexRow>
        )
      },
    },
    {
      key: 'meetingHistory-reviewer',
      title: t('appraisalMeeting.stepOverview.history.reviewer'),
      className: 'minWidth-column-xl',
      dataIndex: 'reviewer',
      render: (reviewer: EmployeeDisplayShortInfo) =>
        !reviewer ? (
          '-'
        ) : (
          <PerAvatarPlus
            nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
            employeeId={reviewer.id}
            firstName={reviewer.firstName}
            lastName={reviewer.lastName}
          />
        ),
    },
    {
      key: 'meetingHistory-actions',
      title: t('appraisalMeeting.stepOverview.history.documents'),
      className: 'minWidth-column-m textAlign-left',
      render: (meeting: Meeting) => {
        return (
          <>
            <PdfDownloadPopover ml="m" mr="xs" meeting={meeting} preparationVisible={canViewPreparation} />
            <PdfObjectiveAttachmentPopover meetingId={meeting.id} employeeId={String(employeeId)} />
          </>
        )
      },
    },
  ]

  const meetingEmployee = emptyTextType === 'meetingEmployee'
  const meetingReviewer = emptyTextType === 'meetingReviewer'
  const request = emptyTextType === 'request'
  const publicProfile = emptyTextType === 'publicProfile'

  const emptyTableActionButton = () => {
    // meeting invite button will be shown in the public profile if the logged in user has rights to manipulate the employee
    if (publicProfile && employee && employeeIsEditable) {
      return <NewMeetingInviteButton employeeList={[employee]} btnType="normal" />
    }
    // request a meeting button will be shown if the logged in user is the employee or if the employee object is undefined
    if (isEmployeeOrEmptyEmployee && !meetingEmployee) {
      return <RequestMeetingInviteButton context="MyMeetings" />
    }

    return ''
  }

  const MeetingHistoryTable = ({ className, scrollY }: { className: string; scrollY: boolean }) => {
    return (
      <Table
        className={className}
        dataSource={meetings.map((meetingHistory) => ({ ...meetingHistory, key: meetingHistory.id }))}
        columns={columns}
        pagination={false}
        scroll={{ y: scrollY }}
        locale={{
          emptyText: (
            <PerEmpty
              icon="chatNo_stroke"
              heading={
                (meetingEmployee ? t('meetingView.ownMeetings.noMeetingsTitleMeetingEmployee') : undefined) ||
                (meetingReviewer
                  ? t('meetingView.ownMeetings.noMeetingsTitleMeetingReviewer', { employee: employeeFirstName })
                  : undefined) ||
                (request
                  ? userCannotRequestMeetings
                    ? t('meetingView.ownMeetings.noMeetingsHistory')
                    : t('meetingView.ownMeetings.noMeetingsTitleRequest')
                  : undefined) ||
                (resignedView ? t('meetingView.ownMeetings.noMeetingsTextResignedEmployee', { name: employee!.firstName }) : undefined) ||
                (publicProfile ? t('meetingView.ownMeetings.noMeetingsTilePublicProfile') : undefined)
              }
              info={
                resignedView
                  ? ''
                  : (meetingEmployee ? t('meetingView.ownMeetings.noMeetingsTextMeetingEmployee') : undefined) ||
                    (meetingReviewer ? t('meetingView.ownMeetings.noMeetingsTextMeetingReviewer') : undefined) ||
                    (request && !userCannotRequestMeetings ? t('meetingView.ownMeetings.noMeetingsTextRequest') : undefined) ||
                    (publicProfile && employeeIsEditable ? t('meetingView.ownMeetings.noMeetingsTextPublicProfile') : undefined)
              }
              button={!userCannotRequestMeetings && !resignedView && emptyTableActionButton()}
            />
          ),
        }}
      />
    )
  }

  return <MeetingHistoryTable className={`small ${light ? 'light meetings-own' : ''}`} scrollY={light ? true : false} />
}
